// OWNED BY the documents implementation agent (see ../DocumentsTab.tsx ownership note).
//
// Data layer of the מסמכים (documents) tab — mock s19. The tab is NET-NEW:
// per-contact file archive stored as
//   Firestore  dashboard-users-v2/{userEmail}/contacts/{intlPhone}/documents/{docId}
//              (a pooled agency's contact: dashboard-orgs/{orgId}/contacts/{intlPhone}/documents/{docId}
//               — see `documentsCollection` below for how the book is learned)
//   Storage    contact-documents/{authUid}/{intlPhone}/{docId}_{fileName}
//
// The upload flow PORTS MediaUpload.tsx's client-side Firebase Storage logic
// (ref → put → getDownloadURL) with two deliberate changes:
//   - the storage path is keyed by the Firebase auth UID, not the raw email
//     (no PII in storage paths — same convention the notes tab voice-upload
//     adopted, mirroring the signature-storage encrypted-owner rule);
//   - a flat 20MB cap + PDF/image/Word accept list per the s19 mock (the
//     legacy WhatsApp-media per-type caps are irrelevant here).
//
// The "חוזים חתומים" group reads COMPLETED signature documents from the SHARED
// `signatures:{contactId}` session-cache key (SignatureDocument[] — the
// listDocuments({contactId}) response). Nothing is copied into the documents
// subcollection; signed PDFs download through the AUTHENTICATED
// /documents/:id/signed-pdf endpoint (never a raw GCS URL — GCS edge caching
// can serve stale bytes after in-place recompose).

import { db, firebaseApp, storage } from "firebaseConfig";
import { GenericConverter } from "utils/firestoreConverters";
import { fData } from "utils/formatNumber";
import { downloadSignedPdfBlob } from "services/signatures";
import { signatureDocumentDisplayName } from "utils/signatureQuestionnaireName";
import { fetchCached, getCached, setCached } from "../../data/sessionCache";
import { documentsBookKey, isOrgBookKey, orgIdOfBookKey } from "hooks/contacts/contactBookRules";
import { resolveContactBook } from "hooks/contacts/useContactBook";
import { CONTACT_DOCUMENTS_SUBCOLLECTION_NAME } from "../../../../../../@types/sharedSchema";
import type {
  ContactDocument,
  ContactDocumentCategory,
  RealEstateContact
} from "../../../../../../@types/sharedSchema";
import type { SignatureDocument } from "../../../../../../@types/signatures";

// ── Cache keys ───────────────────────────────────────────────────────────────
// `documents:{contactId}` is this tab's own key. `signatures:{contactId}` is
// the SHARED key (signatures + documents tabs — see the handbook table). The
// signatures tab owns the CANONICAL fetcher/preload for the shared key (query
// by contact.id, sorted, 403-forbidden-flag gated) — re-exported here so this
// tab never maintains a drifting second implementation (identical key ⇒
// identical value discipline). We remain a tolerant reader of the value.

export const contactDocumentsKey = (contactId: string): string => `documents:${contactId}`;
export {
  signaturesCacheKey as contactSignaturesKey,
  SIGNATURES_MAX_AGE_MS,
  preloadContactSignatures as preloadContactSignatureDocuments
} from "../signatures/signaturesData";

export const DOCUMENTS_MAX_AGE_MS = 60_000;

// ── Upload constraints (mock s19: "PDF, תמונה או Word · עד 20MB") ────────────

export const MAX_UPLOAD_SIZE_BYTES = 20 * 1024 * 1024;
// MIME entries + extension entries: react-dropzone v11 (attr-accept) matches a
// file with an EMPTY File.type (docx on some Windows setups — the exact case
// guessMimeFromName below exists for) by filename suffix ONLY, so without the
// dot-extension entries such valid files are rejected before our fallback runs.
export const ACCEPTED_FILE_TYPES = [
  "application/pdf",
  "image/*",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ".pdf",
  ".doc",
  ".docx",
  ".png",
  ".jpg",
  ".jpeg",
  ".gif",
  ".webp",
  ".heic"
];

/** A ContactDocument as read back from Firestore — id always present. */
export type ContactDocumentWithId = ContactDocument & { id: string };

// ── Firestore access ─────────────────────────────────────────────────────────

/**
 * The subcollection hangs off the contact's BOOK, which is the signed-in email for a
 * personal book and the agency's document for a pooled one (the server's plan D4:
 * `dashboard-orgs/{orgId}/contacts/{phone}/documents`, readable and writable by the
 * agency's members — `firestore.rules`).
 */
const documentsPath = (bookKey: string, contactId: string): string =>
  isOrgBookKey(bookKey)
    ? `dashboard-orgs/${orgIdOfBookKey(bookKey)}/contacts/${contactId}/${CONTACT_DOCUMENTS_SUBCOLLECTION_NAME}`
    : `dashboard-users-v2/${bookKey}/contacts/${contactId}/${CONTACT_DOCUMENTS_SUBCOLLECTION_NAME}`;

const requireAuthUser = (): { email: string; uid: string } => {
  const currentUser = firebaseApp.auth().currentUser;
  const email = currentUser?.email;
  const uid = currentUser?.uid;
  // Throw (not empty result) so fetchCached stores the error and retries later
  // instead of caching a wrong-but-plausible empty list before auth resolves.
  if (!email || !uid) throw new Error("documentsData: no authenticated user yet");
  return { email, uid };
};

/**
 * THE COLLECTION, ONCE THE BOOK IS KNOWN — every read, the listener and every write of this
 * tab go through here.
 *
 * Which book is the server's answer (`GET /contacts/book`, held by the book store). This
 * module is not a hook, and it used to read the store's CURRENT value and fall back to the
 * email: on a cold browser the row-hover preload and the tab's listener both ran before the
 * answer landed, so a pooled member's documents were addressed under their personal path —
 * an empty list cached, a listener on a collection nothing writes to, and an upload filed
 * where nobody else in the office would ever see it. So it WAITS (`resolveContactBook`):
 * for the answer that is on its way, never past a failed ask, and never more than a few
 * seconds.
 *
 * For every personal book the path is the signed-in email, to the letter, as it has always
 * been (`documentsBookKey`) — whether the wait ended in an answer, a failure or the bound.
 *
 * The auth user is read AFTER the wait: it is the account signed in when the path is built.
 */
const documentsCollection = async (contactId: string) => {
  const book = await resolveContactBook();
  const { email, uid } = requireAuthUser();
  return {
    email,
    uid,
    collection: db
      .collection(documentsPath(documentsBookKey(book, email), contactId))
      .withConverter(GenericConverter<ContactDocument>())
  };
};

/** Firestore Timestamp (.toDate), Date, ISO string — never NaN dates. */
export const parseDocTime = (value: unknown): Date => {
  const maybeTimestamp = value as { toDate?: () => Date } | null | undefined;
  if (maybeTimestamp && typeof maybeTimestamp.toDate === "function") {
    return maybeTimestamp.toDate();
  }
  const parsed = new Date(value as string | number | Date);
  return Number.isNaN(parsed.getTime()) ? new Date(0) : parsed;
};

const sortDocsNewestFirst = (docs: ContactDocumentWithId[]): ContactDocumentWithId[] =>
  [...docs].sort(
    (a, b) => parseDocTime(b.uploadedAt).getTime() - parseDocTime(a.uploadedAt).getTime()
  );

/** One-shot fetch of the contact's documents subcollection, newest-first. */
export async function fetchContactDocumentsOnce(
  contactId: string
): Promise<ContactDocumentWithId[]> {
  // Rejects (not an empty result) with no authenticated user, so fetchCached stores the
  // error and retries later instead of caching a wrong-but-plausible empty list.
  const { collection } = await documentsCollection(contactId);
  const snapshot = await collection.orderBy("uploadedAt", "desc").get();
  return snapshot.docs.map((doc) => doc.data() as ContactDocumentWithId);
}

/**
 * Live listener writing into the session cache — the tab body AND the
 * tab-strip badge update together. One light listener on a tiny subcollection;
 * registered on first tab mount, cleaned up when the page unmounts.
 */
export function subscribeToContactDocuments(contactId: string): () => void {
  if (!contactId) return () => undefined;
  // Attached once the book is known (see `documentsCollection`). Until then there is
  // nothing to detach, and an unsubscribe that comes first cancels the attach.
  let cancelled = false;
  let detach: () => void = () => undefined;
  void documentsCollection(contactId)
    .then(({ collection }) => {
      if (cancelled) return;
      detach = collection.orderBy("uploadedAt", "desc").onSnapshot(
        (snapshot) => {
          setCached(
            contactDocumentsKey(contactId),
            snapshot.docs.map((doc) => doc.data() as ContactDocumentWithId)
          );
        },
        (error) => {
          console.error("Documents tab: subcollection listener failed", error);
        }
      );
    })
    // No authenticated user yet: no listener, exactly as before.
    .catch(() => undefined);
  return () => {
    cancelled = true;
    detach();
  };
}

// ── Cache mutators (optimistic updates after writes) ─────────────────────────

const upsertCachedDocument = (contactId: string, docItem: ContactDocumentWithId): void => {
  const current = getCached<ContactDocumentWithId[]>(contactDocumentsKey(contactId)) ?? [];
  const without = current.filter((doc) => doc.id !== docItem.id);
  setCached(contactDocumentsKey(contactId), sortDocsNewestFirst([...without, docItem]));
};

const removeCachedDocument = (contactId: string, docId: string): void => {
  const current = getCached<ContactDocumentWithId[]>(contactDocumentsKey(contactId));
  if (!current) return;
  setCached(
    contactDocumentsKey(contactId),
    current.filter((doc) => doc.id !== docId)
  );
};

// ── Upload (ported from MediaUpload.tsx: ref → put → getDownloadURL) ─────────

/** Storage object names must not contain path/query-breaking chars. Keeps the
 * tail so the extension survives; the pretty name lives on the Firestore doc. */
const sanitizeForStoragePath = (name: string): string => {
  const cleaned = name.replace(/[#[\]*?%\\/\s]+/g, "_");
  return cleaned.slice(-100) || "file";
};

/** Fallback for browsers that hand over an empty File.type (docx on some
 * Windows setups) — the mimeType drives the row icon, so guess from the name. */
const guessMimeFromName = (name: string): string => {
  const ext = name.split(".").pop()?.toLowerCase() ?? "";
  const byExt: Record<string, string> = {
    pdf: "application/pdf",
    doc: "application/msword",
    docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    png: "image/png",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    gif: "image/gif",
    webp: "image/webp",
    heic: "image/heic"
  };
  return byExt[ext] ?? "application/octet-stream";
};

export type UploadProgressCallback = (fraction: number) => void;

/**
 * Uploads one file to Storage, then registers its ContactDocument metadata in
 * the contact's `documents` subcollection. Progress is reported per Storage
 * chunk. If the metadata write fails the uploaded object is deleted
 * (best-effort) so no orphan bytes accumulate.
 */
export async function uploadContactDocument(
  contactId: string,
  file: File,
  onProgress?: UploadProgressCallback
): Promise<ContactDocumentWithId> {
  const { email, uid, collection } = await documentsCollection(contactId);

  const docRef = collection.doc();
  const storagePath = `contact-documents/${uid}/${contactId}/${docRef.id}_${sanitizeForStoragePath(
    file.name
  )}`;
  const fileRef = storage.ref(storagePath);

  const task = fileRef.put(file, { contentType: file.type || guessMimeFromName(file.name) });
  if (onProgress) {
    task.on("state_changed", (snap: { bytesTransferred: number; totalBytes: number }) => {
      if (snap.totalBytes > 0) onProgress(snap.bytesTransferred / snap.totalBytes);
    });
  }
  await task;
  const downloadUrl: string = await fileRef.getDownloadURL();

  const now = new Date();
  const docData: ContactDocument = {
    contactId,
    userId: email,
    name: file.name,
    category: "customerDoc",
    storagePath,
    downloadUrl,
    mimeType: file.type || guessMimeFromName(file.name),
    sizeBytes: file.size,
    uploadedByEmail: email,
    uploadedAt: now,
    createdTime: now
  };

  try {
    await docRef.set(docData);
  } catch (error) {
    // Metadata write failed — remove the just-uploaded object (best-effort).
    void fileRef.delete().catch(() => undefined);
    throw error;
  }

  const withId: ContactDocumentWithId = { ...docData, id: docRef.id };
  upsertCachedDocument(contactId, withId);
  return withId;
}

// ── Mutations on existing documents ──────────────────────────────────────────

export async function renameContactDocument(
  contactId: string,
  docItem: ContactDocumentWithId,
  newName: string
): Promise<void> {
  const { collection } = await documentsCollection(contactId);
  await collection.doc(docItem.id).update({ name: newName });
  upsertCachedDocument(contactId, { ...docItem, name: newName });
}

export async function setContactDocumentCategory(
  contactId: string,
  docItem: ContactDocumentWithId,
  category: ContactDocumentCategory
): Promise<void> {
  const { collection } = await documentsCollection(contactId);
  await collection.doc(docItem.id).update({ category });
  upsertCachedDocument(contactId, { ...docItem, category });
}

/** Deletes the metadata doc, then the storage object (best-effort — a stray
 * storage object is invisible to the UI; a stray metadata doc is not). Not the
 * storage object of a SHARED file (`storageShared`): an office's undo gave it to
 * several realtors over the same bytes, and the others still point at them. */
export async function deleteContactDocument(
  contactId: string,
  docItem: ContactDocumentWithId
): Promise<void> {
  const { collection } = await documentsCollection(contactId);
  await collection.doc(docItem.id).delete();
  removeCachedDocument(contactId, docItem.id);
  if (docItem.storagePath && docItem.storageShared !== true) {
    void storage
      .ref(docItem.storagePath)
      .delete()
      .catch((error) => console.error("Documents tab: failed deleting storage object", error));
  }
}

// ── Signature documents (shared cache key, tolerant reader) ──────────────────
// The fetcher/preload for the shared key are the signatures tab's canonical
// ones (re-exported in the cache-keys section above) — this module only keeps
// the documents-tab-specific derived reader below.

/** COMPLETED, non-archived signature documents, newest-first. Tolerant of a
 * missing/foreign-shaped cache value (the key is shared with another tab). */
export function completedSignatureContracts(
  docs: SignatureDocument[] | undefined
): SignatureDocument[] {
  if (!Array.isArray(docs)) return [];
  return docs
    .filter((doc) => !!doc && doc.status === "completed" && !doc.archivedAt)
    .sort((a, b) =>
      (b.completedAt || b.sentAt || b.createdTime || "").localeCompare(
        a.completedAt || a.sentAt || a.createdTime || ""
      )
    );
}

// ── Preloads (row hover / page mount — idempotent-cheap, never throw) ────────
// (The signatures-key preload is the canonical preloadContactSignatures,
// re-exported as preloadContactSignatureDocuments in the cache-keys section.)

export function preloadContactDocuments(contact: RealEstateContact | null | undefined): void {
  const contactId = contact?.id;
  if (!contactId) return;
  void fetchCached(contactDocumentsKey(contactId), () => fetchContactDocumentsOnce(contactId), {
    maxAgeMs: DOCUMENTS_MAX_AGE_MS
  }).catch(() => undefined);
}

// ── Downloads ────────────────────────────────────────────────────────────────

const triggerBlobDownload = (blob: Blob, fileName: string): void => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
};

/**
 * Downloads an uploaded document with its display name. Firebase Storage
 * download URLs serve CORS-enabled GETs, so fetch→blob preserves the pretty
 * filename; if that ever fails we fall back to opening the tokened URL.
 */
export async function downloadContactDocumentFile(
  docItem: ContactDocumentWithId
): Promise<void> {
  try {
    const response = await fetch(docItem.downloadUrl);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    triggerBlobDownload(await response.blob(), docItem.name);
  } catch {
    // Popup blockers return null from window.open (likely here: after a slow
    // failed fetch the transient user activation has expired). Throw so the
    // row's catch shows the error snackbar instead of silently doing nothing.
    const opened = window.open(docItem.downloadUrl, "_blank", "noopener");
    if (!opened) throw new Error("download fallback was blocked by the popup blocker");
  }
}

/**
 * Downloads a completed signature document through the AUTHENTICATED
 * signed-pdf endpoint (streams via the server — the convention; raw GCS URLs
 * can serve stale bytes and are never handed to the UI).
 */
export async function downloadSignedContractPdf(sigDoc: SignatureDocument): Promise<void> {
  const blob = await downloadSignedPdfBlob(sigDoc.id, "signed");
  const base =
    [sigDoc.formattedNumber, sigDoc.templateNameSnapshot].filter(Boolean).join(" ") ||
    "signed-document";
  triggerBlobDownload(blob, `${base}.pdf`);
}

/**
 * A questionnaire-only document's one file — the filled questionnaire, which IS its signed PDF —
 * through the same authenticated endpoint, named for the client ("שאלון מוכר"), not the template.
 */
export async function downloadQuestionnaireDocumentPdf(sigDoc: SignatureDocument): Promise<void> {
  const blob = await downloadSignedPdfBlob(sigDoc.id, "signed");
  const base =
    [sigDoc.formattedNumber, signatureDocumentDisplayName(sigDoc)].filter(Boolean).join(" ") ||
    "questionnaire";
  triggerBlobDownload(blob, `${base}.pdf`);
}

// ── Display helpers ──────────────────────────────────────────────────────────

export type FileKind = "pdf" | "image" | "word" | "other";

export function fileKindOf(mimeType: string | undefined, name: string | undefined): FileKind {
  const mime = (mimeType ?? "").toLowerCase();
  if (mime.includes("pdf")) return "pdf";
  if (mime.startsWith("image/")) return "image";
  if (mime.includes("msword") || mime.includes("wordprocessingml")) return "word";
  const ext = (name ?? "").split(".").pop()?.toLowerCase() ?? "";
  if (ext === "pdf") return "pdf";
  if (["png", "jpg", "jpeg", "gif", "webp", "heic"].includes(ext)) return "image";
  if (["doc", "docx"].includes(ext)) return "word";
  return "other";
}

/** "PDF" / "JPG" / "DOCX" — the meta-line format label of mock s19. */
export function fileExtensionLabel(name: string | undefined, mimeType: string | undefined): string {
  const ext = (name ?? "").split(".").pop() ?? "";
  if (ext && ext !== name) return ext.toUpperCase().slice(0, 5);
  switch (fileKindOf(mimeType, name)) {
    case "pdf":
      return "PDF";
    case "image":
      return "IMG";
    case "word":
      return "DOC";
    default:
      return "קובץ";
  }
}

/** "1.1 MB" (numeral 0.0 b) — same formatter the legacy upload surfaces use. */
export const formatFileSize = (bytes: number | undefined): string =>
  bytes && bytes > 0 ? fData(bytes) : "";

/** 1.7.2026 — the meta-line date format of the CRM mocks. */
export const formatDocDate = (value: unknown): string => {
  const date = parseDocTime(value);
  if (date.getTime() === 0) return "";
  return `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}`;
};

/** Uploaded within the last 24h — drives the "חדש" label (mock s19). */
export const isRecentUpload = (value: unknown): boolean =>
  Date.now() - parseDocTime(value).getTime() < 24 * 60 * 60 * 1000;

/** "ע״י חיים" — first name of the current user for own uploads, else the
 * email's local part (org managers see the owner's uploads read-only). */
export function uploaderLabel(
  uploadedByEmail: string | undefined,
  currentEmail: string | undefined,
  currentDisplayName: string | undefined
): string {
  if (!uploadedByEmail) return "";
  if (uploadedByEmail === currentEmail && currentDisplayName) {
    const firstName = currentDisplayName.trim().split(/\s+/)[0];
    if (firstName) return firstName;
  }
  return uploadedByEmail.split("@")[0];
}

/** Sum of stored file sizes — the card-header caption ("3 קבצים · 4.2MB"). */
export const totalSizeBytes = (docs: ContactDocumentWithId[]): number =>
  docs.reduce((acc, doc) => acc + (doc.sizeBytes > 0 ? doc.sizeBytes : 0), 0);
